import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Mic, MicOff, Volume2, VolumeX, AlertTriangle, Loader2 } from 'lucide-react';

const AudioProctoring = ({
  enabled = true,
  sensitivity = 'moderate', // 'strict', 'moderate', 'relaxed'
  onViolation,
  onStatusChange,
  violationThreshold = 10,
  attemptId,
  challengeId
}) => {
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const rafIdRef = useRef(null);
  const consecutiveHighRef = useRef(0);
  
  const [isListening, setIsListening] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(0);
  const [violations, setViolations] = useState(0);
  const [status, setStatus] = useState('initializing');
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Sensitivity thresholds (0-100 scale)
  const thresholds = {
    strict: { talking: 15, background: 8 },      // Very sensitive
    moderate: { talking: 35, background: 20 },   // Balanced
    relaxed: { talking: 55, background: 35 }     // Lenient
  };

  const currentThreshold = thresholds[sensitivity] || thresholds.moderate;

  useEffect(() => {
    if (enabled) {
      startListening();
    }

    return () => {
      stopListening();
    };
  }, [enabled]);

  const startListening = async () => {
    try {
      setStatus('requesting_mic');
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: false, // We want to detect noise
          autoGainControl: false
        } 
      });
      
      micStreamRef.current = stream;
      
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      
      // Create analyser
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;
      
      // Connect microphone to analyser
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      setIsListening(true);
      setStatus('active');
      
      // Start monitoring
      monitorAudio();
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setStatus('mic_error');
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
        handleViolation('mic_blocked', 'Microphone access denied');
      }
    }
  };

  const stopListening = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    setIsListening(false);
  };

  const monitorAudio = useCallback(() => {
    if (!analyserRef.current || !isListening) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const analyze = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
      const normalizedVolume = Math.min(100, (average / 128) * 100);
      
      setCurrentVolume(normalizedVolume);
      
      // Check for violations based on sensitivity
      if (normalizedVolume > currentThreshold.talking) {
        consecutiveHighRef.current++;
        
        // Require sustained high volume (2+ seconds at 60fps = ~120 frames)
        // We check every ~100ms, so 20 consecutive checks = 2 seconds
        if (consecutiveHighRef.current >= 20) {
          handleViolation('talking_detected', `Voice/talking detected (volume: ${normalizedVolume.toFixed(0)}%)`);
          consecutiveHighRef.current = 0;
        }
      } else if (normalizedVolume > currentThreshold.background) {
        // Background noise - less severe
        consecutiveHighRef.current++;
        if (consecutiveHighRef.current >= 50) { // 5 seconds of sustained background noise
          handleViolation('background_noise', `High background noise detected`);
          consecutiveHighRef.current = 0;
        }
      } else {
        consecutiveHighRef.current = Math.max(0, consecutiveHighRef.current - 1);
      }
      
      rafIdRef.current = requestAnimationFrame(analyze);
    };
    
    // Start analysis loop with throttling
    const throttledAnalyze = () => {
      analyze();
    };
    
    rafIdRef.current = requestAnimationFrame(throttledAnalyze);
  }, [isListening, currentThreshold]);

  useEffect(() => {
    if (isListening) {
      monitorAudio();
    }
  }, [isListening, monitorAudio]);

  const handleViolation = useCallback((type, message) => {
    const newViolationCount = violations + 1;
    setViolations(newViolationCount);
    
    // Show warning
    setWarningMessage(message);
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 3000);
    
    // Notify parent
    if (onViolation) {
      onViolation({
        type,
        message,
        timestamp: new Date().toISOString(),
        attemptId,
        challengeId,
        volume: currentVolume
      });
    }
    
    // Update status
    if (onStatusChange) {
      onStatusChange({
        totalViolations: newViolationCount,
        isTerminated: newViolationCount >= violationThreshold
      });
    }
  }, [violations, currentVolume, onViolation, onStatusChange, attemptId, challengeId, violationThreshold]);

  const getVolumeColor = () => {
    if (currentVolume > currentThreshold.talking) return 'bg-red-500';
    if (currentVolume > currentThreshold.background) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getStatusIcon = () => {
    if (permissionDenied) return <MicOff className="text-red-400" size={16} />;
    if (!isListening) return <MicOff className="text-gray-500" size={16} />;
    if (currentVolume > currentThreshold.talking) return <Volume2 className="text-red-400 animate-pulse" size={16} />;
    return <Mic className="text-green-400" size={16} />;
  };

  if (!enabled) return null;

  return (
    <div className="relative">
      {/* Audio Monitor */}
      <div className="p-3 bg-white/5 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <span className="text-xs text-gray-400">Audio Monitor</span>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded ${
            violations >= violationThreshold * 0.7 
              ? 'bg-red-500/20 text-red-400' 
              : 'bg-white/10 text-gray-400'
          }`}>
            {violations}/{violationThreshold}
          </span>
        </div>
        
        {/* Volume Meter */}
        <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-100 ${getVolumeColor()}`}
            style={{ width: `${Math.min(100, currentVolume)}%` }}
          />
          {/* Threshold markers */}
          <div 
            className="absolute top-0 h-full w-0.5 bg-amber-500/50"
            style={{ left: `${currentThreshold.background}%` }}
          />
          <div 
            className="absolute top-0 h-full w-0.5 bg-red-500/50"
            style={{ left: `${currentThreshold.talking}%` }}
          />
        </div>
        
        {/* Status Text */}
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className={`${
            status === 'active' ? 'text-green-400' :
            status === 'mic_error' ? 'text-red-400' :
            'text-gray-500'
          }`}>
            {status === 'active' ? 'Listening' :
             status === 'mic_error' ? 'Mic Error' :
             status === 'requesting_mic' ? 'Requesting...' :
             'Initializing'}
          </span>
          <span className="text-gray-500">
            {sensitivity.charAt(0).toUpperCase() + sensitivity.slice(1)} mode
          </span>
        </div>
      </div>

      {/* Warning Banner */}
      {showWarning && (
        <div className="absolute -bottom-12 left-0 right-0 bg-amber-500 text-black p-2 rounded-lg flex items-center gap-2 animate-pulse shadow-lg z-10">
          <AlertTriangle size={14} />
          <span className="text-xs font-medium flex-1">{warningMessage}</span>
        </div>
      )}
    </div>
  );
};

export default AudioProctoring;
